import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { profileService } from '../../services/profileService'
import { resumeService } from '../../services/resumeService'
import { getProfileUrl } from '../../utils/url'
import { useProfileContext } from '../layout/AuthenticatedLayout'
import ProcessingModal from '../resume/ProcessingModal'

interface ResumeUploadCardProps {
  title?: string
  subtitle?: string
  icon?: React.ReactNode
}

const stages = [
  { progress: 5, text: 'Uploading your resume...' },
  { progress: 20, text: 'Reading your document...' },
  { progress: 40, text: 'Extracting your experience...' },
  { progress: 60, text: 'Reviewing your education...' },
  { progress: 75, text: 'Discovering your projects...' },
  { progress: 90, text: 'Building your profile...' },
  { progress: 95, text: 'Adding finishing touches...' },
]

export default function ResumeUploadCard({
  title = 'No profile yet',
  subtitle = 'Upload your resume to create your professional profile.',
  icon,
}: ResumeUploadCardProps) {
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [isCreatingEmpty, setIsCreatingEmpty] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { setProfile } = useProfileContext()

  const handleFileChange = (selectedFile: File | null) => {
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Please select a PDF file')
        setFile(null)
        return
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File must be less than 10MB')
        setFile(null)
        return
      }
      setFile(selectedFile)
      setError('')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e.target.files?.[0] || null)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileChange(e.dataTransfer.files?.[0] || null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const simulateProgress = () => {
    let currentStage = 0
    const interval = setInterval(() => {
      if (currentStage < stages.length) {
        setProgress(stages[currentStage].progress)
        setStage(stages[currentStage].text)
        currentStage++
      }
    }, 2500)
    return interval
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!file) {
      setError('Please select a file')
      return
    }

    setIsProcessing(true)
    setError('')
    const progressInterval = simulateProgress()

    try {
      const response = await resumeService.uploadResume(file)
      clearInterval(progressInterval)
      setProgress(100)
      setStage('Complete!')

      setTimeout(() => {
        window.location.href = getProfileUrl(response.profile.username)
      }, 1000)
    } catch (err: unknown) {
      clearInterval(progressInterval)
      setIsProcessing(false)
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Upload failed. Please try again.')
    }
  }

  const handleCreateWithoutResume = async () => {
    setIsCreatingEmpty(true)
    setError('')

    try {
      const profile = await profileService.createEmptyProfile()
      setProfile(profile)
      navigate('/edit')
    } catch (err: unknown) {
      setIsCreatingEmpty(false)
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to create profile. Please try again.')
    }
  }

  const defaultIcon = (
    <svg className="w-8 h-8 text-black/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  )

  return (
    <>
      <ProcessingModal isOpen={isProcessing} progress={progress} stage={stage} />

      <div className="text-center py-12 max-w-md mx-auto">
        <div className="w-16 h-16 bg-black/10 rounded-full flex items-center justify-center mx-auto mb-6">
          {icon || defaultIcon}
        </div>
        <h2
          className="text-2xl text-black mb-2"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          {title}
        </h2>
        <p className="text-black/60 mb-6">{subtitle}</p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300">
            <p className="text-xs text-red-800 flex items-center justify-center gap-1.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </p>
          </div>
        )}

        {/* Upload Form */}
        <form onSubmit={handleSubmit}>
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-xl p-4 cursor-pointer transition-all ${
              isDragging
                ? 'border-black bg-black/5'
                : file
                  ? 'border-black bg-black/5'
                  : 'border-black/20 hover:border-black/40 hover:bg-black/5'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleInputChange}
              className="hidden"
            />

            {file ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-black/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-black truncate">{file.name}</p>
                  <p className="text-xs text-black/60">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setFile(null)
                    }}
                    className="text-xs text-black/70 hover:text-black font-medium mt-0.5"
                  >
                    Choose a different file
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-black/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-black">Drop your resume here</p>
                  <p className="text-xs text-black/60 mt-0.5">
                    or <span className="text-black font-medium">browse</span> to choose a file
                  </p>
                  <p className="text-xs text-black/50 mt-0.5">PDF files up to 10MB</p>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!file || isProcessing}
            className="w-full mt-4 py-2.5 px-4 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
          >
            <span className="flex items-center justify-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Create my profile
            </span>
          </button>

          {/* Create without resume link */}
          <p className="text-center mt-4">
            <button
              type="button"
              onClick={handleCreateWithoutResume}
              disabled={isCreatingEmpty || isProcessing}
              className="text-sm text-black/60 hover:text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreatingEmpty ? 'Creating...' : 'Create profile without resume'}
            </button>
          </p>
        </form>
      </div>
    </>
  )
}
